import React, { useState, useMemo } from 'react';
import { useFamilyContext } from '../contexts/FamilyContext';
import { useTimelineTasks } from '../hooks/useTimelineTasks';
import { supabase } from '../lib/supabase';
import WorkflowOrganizationService from '../services/WorkflowOrganizationService';
import RecoveryModeBanner from '../components/Timeline/RecoveryModeBanner';

/**
 * TimelineDevPage
 *
 * Development page for testing and refining sprint planning features
 * including the orange "focused plan" RecoveryModeBanner and "Your Next Action" section.
 *
 * Access via: /college/timeline-dev (not shown in main navigation)
 */
const TimelineDevPage = () => {
  const { currentUser } = useFamilyContext();
  const { timeline, loading, error, refetch } = useTimelineTasks(currentUser?.id);

  const handleTaskUpdate = async (taskId, updates) => {
    try {
      const updatePromise = supabase
        .from('timeline_items')
        .update(updates)
        .eq('id', taskId);

      await refetch();

      const { error: updateError } = await updatePromise;
      if (updateError) {
        console.error('Error updating task:', updateError);
        await refetch();
      }
    } catch (err) {
      console.error('Error updating task:', err);
      await refetch();
    }
  };

  // Organize timeline items into workflows
  const workflows = useMemo(() => {
    return WorkflowOrganizationService.organizeIntoWorkflows(timeline || []);
  }, [timeline]);

  // Extract unique colleges from timeline items
  const colleges = useMemo(() => {
    const collegeMap = new Map();
    (timeline || []).forEach(item => {
      if (item.college && item.college.id) {
        const gc = item.college.global_colleges;
        const applicationRound = item.college.application_round;
        const collegeName = gc?.name || item.college.name;

        collegeMap.set(item.college.id, {
          id: item.college.id,
          name: collegeName,
          college_name: collegeName,
          application_round: applicationRound,
          category: item.college.category,
          priority_order: item.college.priority_order,
          global_colleges: gc,
          deadlines: {
            ed: gc?.ed_deadline,
            ed2: gc?.ed2_deadline,
            ea: gc?.ea_deadline,
            rd: gc?.rd_deadline
          }
        });
      }
    });
    return Array.from(collegeMap.values());
  }, [timeline]);

  // Calculate recovery metrics
  const recoveryMetrics = useMemo(() => {
    return WorkflowOrganizationService.calculateRecoveryMetrics(workflows);
  }, [workflows]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: "'Reddit Sans', system-ui, -apple-system, sans-serif",
        color: '#64748b'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #f5f5f4',
            borderTop: '4px solid #26a69a',
            borderRadius: '50%',
            margin: '0 auto 1rem',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <div>Loading dev timeline...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: "'Reddit Sans', system-ui, -apple-system, sans-serif"
      }}>
        <div style={{
          background: 'white',
          border: '2px solid #fccfcf',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ color: '#0f172a', marginBottom: '0.5rem' }}>Error Loading Timeline</h2>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>{error.message}</p>
          <button
            onClick={refetch}
            style={{
              background: '#26a69a',
              color: 'white',
              border: '2px solid #0f172a',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem',
      fontFamily: "'Reddit Sans', system-ui, -apple-system, sans-serif"
    }}>
      {/* Dev Page Header */}
      <div style={{
        background: '#fbbf24',
        border: '2px solid #0f172a',
        borderRadius: '12px',
        padding: '1rem 1.5rem',
        marginBottom: '2rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <span style={{ fontSize: '1.5rem' }}>🔧</span>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
            Timeline Dev Page
          </h1>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#78350f' }}>
            Development environment for sprint planning features • Route: /college/timeline-dev
          </p>
        </div>
      </div>

      {/* Orange Recovery/Sprint Banner */}
      {recoveryMetrics.isInRecoveryMode && (
        <RecoveryModeBanner
          tasks={timeline || []}
          colleges={colleges}
          className="mb-8"
        />
      )}

      {/* Your Next Action Section */}
      {workflows.some(w => w.nextAction) && (
        <div style={{
          background: 'linear-gradient(135deg, #26a69a 0%, #1a6b65 100%)',
          border: '2px solid #0f172a',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
              Your Next Action
            </h2>
          </div>
          <p style={{ opacity: 0.95, marginBottom: '1.5rem' }}>
            This is the single most important thing you can do right now
          </p>

          {(() => {
            const workflowWithNext = workflows.find(w => w.nextAction);
            if (!workflowWithNext) return null;
            const nextAction = workflowWithNext.nextAction;

            return (
              <div style={{
                background: 'white',
                border: '2px solid #0f172a',
                borderRadius: '10px',
                padding: '1.5rem',
                color: '#0f172a'
              }}>
                <div style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  {nextAction.title}
                </div>
                <div style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  {nextAction.context}
                </div>
                {nextAction.step?.description && (
                  <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
                    {nextAction.step.description}
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Debug Info */}
      <div style={{
        background: 'white',
        border: '2px solid #e5e7eb',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
          Debug Info
        </h3>
        <div style={{ fontSize: '0.875rem', color: '#64748b', display: 'grid', gap: '0.5rem' }}>
          <div><strong>Total Tasks:</strong> {timeline?.length || 0}</div>
          <div><strong>Colleges:</strong> {colleges.length}</div>
          <div><strong>Workflows:</strong> {workflows.length}</div>
          <div><strong>Recovery Mode:</strong> {recoveryMetrics.isInRecoveryMode ? 'Yes' : 'No'}</div>
          <div><strong>Has Next Action:</strong> {workflows.some(w => w.nextAction) ? 'Yes' : 'No'}</div>
        </div>
      </div>

      {/* Link to Main Timeline */}
      <div style={{ textAlign: 'center' }}>
        <a
          href="/college/timeline"
          style={{
            display: 'inline-block',
            background: '#26a69a',
            color: 'white',
            border: '2px solid #0f172a',
            borderRadius: '8px',
            padding: '0.75rem 1.5rem',
            fontSize: '0.9375rem',
            fontWeight: 600,
            textDecoration: 'none',
            transition: 'all 0.2s'
          }}
        >
          ← Back to Main Timeline
        </a>
      </div>
    </div>
  );
};

export default TimelineDevPage;
